import { ActionTree } from "vuex";
import {
  RootState,
  MypageState,
  MypageFolderOperationResult,
  MypageOperationResult,
  MypageRecipeClips,
  MypageFolders,
  MypageFolderClips,
  MypageAddFolderRecipe,
  MypageDeleteFolderRecipe,
  MypageShoppingListRegistListItem,
  MypageHistoryRecipes,
  MypageStoreFoodsRanking,
  MypageStoreUserRanking
} from "@/types/mypage";
import store from "@/store";
import apiClient from "@/client/api-client";
import { API_URL_RECIPE, API_URL_SHOPPING_LIST, API_URL_INQUIRY } from "@/const/api-const";
import NativeClient from "@/client/native-client";
import { getApplianceListText } from "@/utils/search-util";
import { getEndOfLastMonth } from "@/utils/mypage-util";
import DateUtil from "@/utils/date-util";
import { getPreviou6Date, getNextDate } from "@/utils/mypage-util";

const actions: ActionTree<MypageState, RootState> = {
  // レシピ登録
  addRecipe: async ({ commit }, recipeId: number) => {
    // レシピ削除API呼び出し
    const res = await apiClient.post(API_URL_RECIPE.MYLIST_RECIPE_ADD, {
      // レシピID
      recipeId: recipeId
    });
    commit("updateAddRecipe", res.data);
  },
  updateAddRecipe: async ({ commit }, result: MypageOperationResult | null) => {
    commit("updateAddRecipe", result);
  },

  // レシピ削除
  deleteRecipe: async ({ commit }, recipeId: number) => {
    const result: MypageOperationResult | null = store.getters["mypage/deleteRecipe"];

    // レシピ削除API呼び出し
    const res = await apiClient.post(API_URL_RECIPE.MYLIST_RECIPE_DELETE, {
      recipeId: recipeId
    });
    commit("updateDeleteRecipe", res.data);
  },
  updateDeleteRecipe: async ({ commit }, result: MypageOperationResult | null) => {
    commit("updateDeleteRecipe", result);
  },

  // お気に入り一覧取得（トップ用、最近追加したもの）
  getRecentFavoriteRecipeList: async ({ commit }) => {
    // お気に入りレシピ取得API呼び出し
    const apiParams = store.getters["common/getApiBaseParams"];
    const applianceText = getApplianceListText(apiParams.applianceList);
    const res = await apiClient.get(API_URL_RECIPE.MYLIST, {
      params: {
        // My家電ID
        myAppliance: (applianceText != null) ? applianceText : null,
        // 「ホーム > マイリスト」画面と「お気に入りを追加 > マイリストから選ぶ」 の画面で、表示するレシピを出し分けるために利用するパラメータ。
        needDlMenuId: false,
        // 取得開始位置（すでに取得済みの場合、取得した件数以降の開始位置を指定）
        start: 0,
        // 10件
        row: 10
      }
    });
    NativeClient.sendDebugLog("Action.ts getRecentFavoriteRecipeList recipeList.count = ");

    commit("updateRecentFavoriteRecipeList", res.data);
  },
  updateRecentFavoriteRecipeList: async ({ commit }, clips: MypageRecipeClips | null) => {
    commit("updateRecentFavoriteRecipeList", clips);
  },

  // お気に入り一覧取得（追加読み込み対応）
  getFavoriteRecipeList: async ({ commit }) => {
    const rowOneTime = 10;
    const apiParams = store.getters["common/getApiBaseParams"];

    const previous: MypageRecipeClips | null = store.getters["mypage/getFavoriteRecipeList"];

    // 全取得済み判定と今回の取得開始位置
    let startPosition = 0;
    if (previous != null) {
      // 前回のデータが存在する
      if (previous.start + rowOneTime >= previous.totalCount) {
        // 既に全件取得ずみ
        return;
      }
      startPosition = previous.start + rowOneTime;
    }

    // お気に入りレシピ取得API呼び出し
    const applianceText = getApplianceListText(apiParams.applianceList);
    const res = await apiClient.get(API_URL_RECIPE.MYLIST, {
      params: {
        // My家電ID
        myAppliance: (applianceText != null) ? applianceText : null,
        // 「ホーム > マイリスト」画面と「お気に入りを追加 > マイリストから選ぶ」 の画面で、表示するレシピを出し分けるために利用するパラメータ。
        needDlMenuId: false,
        // 取得開始位置（すでに取得済みの場合、取得した件数以降の開始位置を指定）
        start: startPosition,
        // 10件
        row: rowOneTime
      }
    });

    commit("updateFavoriteRecipeList", res.data);
  },
  updateFavoriteRecipeList: async ({ commit }, clips: MypageRecipeClips | null) => {
    commit("updateFavoriteRecipeList", clips);
  },

  // フォルダ一覧
  getFolderList: async ({ commit }) => {
    const apiParams = store.getters["common/getApiBaseParams"];
    // お気に入りフォルダ一取得API呼び出し
    const applianceText = getApplianceListText(apiParams.applianceList);
    const res = await apiClient.get(API_URL_RECIPE.MYLIST_FOLDER_LIST, {
      params: {
        myAppliance: (applianceText != null) ? applianceText : null
      }
    });

    commit("updateFolderList", res.data);
  },
  updateFolderList: async ({ commit }, folders: MypageFolders | null) => {
    commit("updateFolderList", folders);
  },

  // フォルダ登録
  registFolder: async ({ commit }, name: string) => {
    NativeClient.sendDebugLog("Action.ts registFolder name = " + name);
    // お気に入りフォルダ登録API呼び出し
    const res = await apiClient.post(API_URL_RECIPE.MYLIST_FOLDER_REGIST, {
      // フォルダ名
      name: name
    });
    commit("updateRegistFolder", res.data);
  },
  updateRegistFolder: async ({ commit }, result: MypageFolderOperationResult | null) => {
    commit("updateRegistFolder", result);
  },

  // フォルダ内のレシピ取得
  getFolderRecipeList: async ({ commit }, folderId: number) => {
    const rowOneTime = 10;
    const apiParams = store.getters["common/getApiBaseParams"];
    // フォルダIDをパスに設定
    NativeClient.sendDebugLog("Action.ts getFolderRecipeList folderId = " + folderId);
    const apiPath = API_URL_RECIPE.MYLIST_FOLDER_RECIPE_LIST.replace("$folderId", folderId.toString());
    NativeClient.sendDebugLog("Action.ts getFolderRecipeList path = " + apiPath);

    const previous: MypageFolderClips | null = store.getters["mypage/getFolderRecipeList"];

    // 全取得済み判定と今回の取得開始位置
    let startPosition = 0;
    if (previous != null) {
      // 前回のデータが存在する
      if (previous.start + rowOneTime >= previous.totalCount) {
        // 既に全件取得ずみ
        return;
      }
      startPosition = previous.start + rowOneTime;
    }

    // お気に入りレシピ取得API呼び出し
    const applianceText = getApplianceListText(apiParams.applianceList);
    const res = await apiClient.get(apiPath, {
      params: {
        myAppliance: (applianceText != null) ? applianceText : null,
        start: startPosition,
        row: rowOneTime
      }
    });
    NativeClient.sendDebugLog("Action.ts getFolderRecipeList response = " + JSON.stringify(res.data));
    commit("updateFolderRecipeList", res.data);
  },
  updateFolderRecipeList: async ({ commit }, clips: MypageFolderClips | null) => {
    commit("updateFolderRecipeList", clips);
  },

  // お気に入りフォルダ更新（名前変更）
  renameFolder: async ({ commit }, params: { folderId: number, folderName: string }) => {
    // フォルダIDをパスに設定
    const apiPath = API_URL_RECIPE.MYLIST_FOLDER_RENAME.replace("$folderId", params.folderId.toString());

    // お気に入りレシピ取得API呼び出し
    const res = await apiClient.put(apiPath, {
      // フォルダ名
      name: params.folderName
    });

    commit("updateRenameFolder", res.data);
  },
  updateRenameFolder: async ({ commit }, result: MypageFolderOperationResult | null) => {
    commit("updateRenameFolder", result);
  },

  // お気に入りフォルダ削除
  deleteFolder: async ({ commit }, folderId: number) => {
    // フォルダIDをパスに設定
    const apiPath = API_URL_RECIPE.MYLIST_FOLDER_DELETE.replace("$folderId", folderId.toString());

    // お気に入りレシピ取得API呼び出し
    const res = await apiClient.delete(apiPath);

    commit("updateDeleteFolder", res.data);
  },
  updateDeleteFolder: async ({ commit }, result: MypageFolderOperationResult | null) => {
    commit("updateDeleteFolder", result);
  },

  // お気に入りフォルダへのレシピ追加
  addFolderRecipe: async ({ commit }, params: { folderId: number, recipes: MypageAddFolderRecipe[] }) => {
    // フォルダIDをパスに設定
    for (const recipeId of params.recipes) {
      NativeClient.sendDebugLog("Action.ts addFolderRecipe recipeIds = " + recipeId);
    }
    const apiPath = API_URL_RECIPE.MYLIST_FOLDER_ADD_RECIPE.replace("$folderId", params.folderId.toString());
    NativeClient.sendDebugLog("Action.ts addFolderRecipe path = " + apiPath);

    // お気に入りレシピ取得API呼び出し
    const res = await apiClient.post(apiPath, {
      recipes: params.recipes
    });
    NativeClient.sendDebugLog("Action.ts addFolderRecipe response = " + JSON.stringify(res.data));
    commit("updateAddFolderRecipe", res.data);
  },
  updateAddFolderRecipe: async ({ commit }, result: MypageFolderOperationResult | null) => {
    commit("updateAddFolderRecipe", result);
  },

  // お気に入りフォルダ削除
  deleteFolderRecipe: async ({ commit }, params: { folderId: number, recipeIds: number[] }) => {
    // フォルダIDをパスに設定
    NativeClient.sendDebugLog("Action.ts deleteFolderRecipe folderId = " + params.folderId + ", recipeId = " + params.recipeIds[0]);
    const apiPath = API_URL_RECIPE.MYLIST_FOLDER_REMOVE_RECIPE.replace("$folderId", params.folderId.toString());
    NativeClient.sendDebugLog("Action.ts deleteFolderRecipe path = " + apiPath);

    const recipeIdArray: MypageDeleteFolderRecipe[] = [];
    for (const recipeId of params.recipeIds) {
      const element = new MypageDeleteFolderRecipe(recipeId);
      recipeIdArray.push(element);
    }

    // お気に入りレシピ取得API呼び出し
    const res = await apiClient.post(apiPath, {
      recipes: recipeIdArray
    });
    NativeClient.sendDebugLog("Action.ts deleteFolderRecipe end ");
    commit("updateDeleteFolderRecipe", res.data);
  },
  updateDeleteFolderRecipe: async ({ commit }, result: MypageFolderOperationResult | null) => {
    commit("updateDeleteFolderRecipe", result);
  },

  // お気に入りフォルダ削除
  moveFolder: async ({ commit }, params: { folderId: number, position: number }) => {
    // フォルダIDをパスに設定
    const apiPath = API_URL_RECIPE.MYLIST_FOLDER_SORT.replace("$folderId", params.folderId.toString());

    // お気に入りレシピ取得API呼び出し
    const res = await apiClient.put(apiPath, {
      position: params.position
    });

    commit("updateMoveFolder", res.data);
  },
  updateMoveFolder: async ({ commit }, result: MypageOperationResult | null) => {
    commit("updateMoveFolder", result);
  },
  updateFolderName: async ({ commit }, folderName: string | null) => {
    commit("updateFolderName", folderName);
  },
  updateSelectedTab: ({ commit }, selected: number) => {
    commit("updateSelectedTab", selected);
  },
  // お気に入り食品取得
  getFavoriteFoodList: async ({ commit }) => {
    const apiParams = store.getters["common/getApiBaseParams"];
    // お気に入り食品取得API呼び出し
    const applianceText = getApplianceListText(apiParams.applianceList);
    const res = await apiClient.get(API_URL_RECIPE.FAVORITE_FOODS, {
      params: {
        // 並び順は、登録順
        order: "newest",
        start: 0
      }
    });
    NativeClient.sendDebugLog("Action.ts getFavoriteFoodList response = " + JSON.stringify(res.data));
    commit("updateFavoriteFoodList", res.data);
  },
  // お気に入り食品削除
  deleteFavoriteFood: async ({ commit }, foodId: number) => {
    const apiParams = store.getters["common/getApiBaseParams"];
    const apiPath = API_URL_RECIPE.FAVORITE_FOOD + `?foodId=${foodId}`;
    // お気に入り食品削除API呼び出し
    const res = await apiClient.delete(apiPath);
    NativeClient.sendDebugLog("Action.ts deleteFavoriteFood response = " + JSON.stringify(res.data));
  },
  // 買い物リスト登録
  registShoppingList: async ({ commit }, foods: Array<MypageShoppingListRegistListItem>) => {
    NativeClient.sendDebugLog("Action.ts registShoppingList list = " +  JSON.stringify(foods));
    const res = await apiClient.post(API_URL_SHOPPING_LIST.FOODS_REGIST, {
      foods
    });
  },
  // 問診回答状態取得
  getInquiryStatus: async ({ commit }) => {
    const apiParams = store.getters["common/getApiBaseParams"];
    const apiPath = API_URL_INQUIRY.GET_STATUS;
    // お気に入り食品削除API呼び出し
    const res = await apiClient.get(apiPath, {
      params: {
        version: "1.0.0",
        appVersion: "1.0.0"
      }
    });
    NativeClient.sendDebugLog("Action.ts getInquiryStatus response = " + JSON.stringify(res.data));
    commit("updateInquiryStatus", res.data);
  },
  // ユーザー食品消費ランキング取得
  getUsedFoodsRanking: async ({ commit }) => {
    const endOfLastMonth = getEndOfLastMonth();
    const startDate = new Date(endOfLastMonth.getFullYear(), endOfLastMonth.getMonth(), 1);
    const startDateString = DateUtil.dateYYYYMMDDWithHiphen(startDate);
    const apiPath = API_URL_RECIPE.USED_FOOD_RANKING;
    const res = await apiClient.get(apiPath, {
      params: {
        startDate: startDateString,
        period: "monthly",
        target: "personal",
        kind: "used"
      }
    });
    NativeClient.sendDebugLog("Action.ts getUsedFoodsRanking response = " + JSON.stringify(res.data));
    commit("updateUsedFoodsRanking", res.data);
  },
  // 個人月別ユーザー食品消費ランキング取得
  getPersonalMonthlyUsedFoodsRanking: async ({ commit }, yearMonth: string) => {
    const startDateString = `${yearMonth}-01`;
    const apiPath = API_URL_RECIPE.USED_FOOD_RANKING;
    const res = await apiClient.get(apiPath, {
      params: {
        startDate: startDateString,
        period: "monthly",
        target: "personal",
        kind: "used"
      }
    });
    NativeClient.sendDebugLog("Action.ts getPersonalMonthUsedFoodsRanking month = " + yearMonth + ", response = " + JSON.stringify(res.data));
    const storeData = new MypageStoreFoodsRanking();
    storeData.month = yearMonth;
    storeData.data = res.data;
    commit("updatePersonalMonthlyUsedFoodsRanking", storeData);
  },
  // 個人月別ユーザー食品廃棄ランキング取得
  getPersonalMonthlyDisposedFoodsRanking: async ({ commit }, yearMonth: string) => {
    const startDateString = `${yearMonth}-01`;
    const apiPath = API_URL_RECIPE.USED_FOOD_RANKING;
    const res = await apiClient.get(apiPath, {
      params: {
        startDate: startDateString,
        period: "monthly",
        target: "personal",
        kind: "dispose"
      }
    });
    NativeClient.sendDebugLog("Action.ts getPersonalMonthlyDisposedFoodsRanking month = " + yearMonth + ", response = " + JSON.stringify(res.data));
    const storeData = new MypageStoreFoodsRanking();
    storeData.month = yearMonth;
    storeData.data = res.data;
    commit("updatePersonalMonthlyDisposedFoodsRanking", storeData);
  },
  // 全国月別ユーザー食品消費ランキング取得
  getNationwideMonthlyUsedFoodsRanking: async ({ commit }, yearMonth: string) => {
    const startDateString = `${yearMonth}-01`;
    const apiPath = API_URL_RECIPE.USED_FOOD_RANKING;
    const res = await apiClient.get(apiPath, {
      params: {
        startDate: startDateString,
        period: "monthly",
        target: "all",
        kind: "used"
      }
    });
    NativeClient.sendDebugLog("Action.ts getNationwideMonthlyUsedFoodsRanking  month = " + yearMonth + ", response = " + JSON.stringify(res.data));
    const storeData = new MypageStoreFoodsRanking();
    storeData.month = yearMonth;
    storeData.data = res.data;
    commit("updateNationwideMonthlyUsedFoodsRanking", storeData);
  },
  // 全国月別ユーザー食品廃棄ランキング取得
  getNationwideMonthlyDisposedFoodsRanking: async ({ commit }, yearMonth: string) => {
      const startDateString = `${yearMonth}-01`;
      const apiPath = API_URL_RECIPE.USED_FOOD_RANKING;
      const res = await apiClient.get(apiPath, {
        params: {
          startDate: startDateString,
          period: "monthly",
          target: "all",
          kind: "dispose"
        }
      });
      NativeClient.sendDebugLog("Action.ts getNationwideMonthlyDisposedFoodsRanking  month = " + yearMonth + ", response = " + JSON.stringify(res.data));
      const storeData = new MypageStoreFoodsRanking();
      storeData.month = yearMonth;
      storeData.data = res.data;
      commit("updateNationwideMonthlyDisposedFoodsRanking", storeData);
    },
  // ユーザー食品消費度順位取得
  getUserRanking: async ({ commit }) => {
    const endOfLastMonth = getEndOfLastMonth();
    const startDate = new Date(endOfLastMonth.getFullYear(), endOfLastMonth.getMonth(), 1);
    const startDateString = DateUtil.dateYYYYMMDDWithHiphen(startDate);
    const apiPath = API_URL_RECIPE.USER_RANKING;
    const res = await apiClient.get(apiPath, {
      params: {
        startDate: startDateString,
        period: "monthly"
      }
    });
    NativeClient.sendDebugLog("Action.ts getUserRanking response = " + JSON.stringify(res.data));
    commit("updateUserRanking", res.data);
  },
  // 月別ユーザー食品消費度順位取得A
  getMonthlyUserRanking: async ({ commit }, yearMonth: string) => {
    const startDateString = `${yearMonth}-01`;
    const apiPath = API_URL_RECIPE.USER_RANKING;
    const res = await apiClient.get(apiPath, {
      params: {
        startDate: startDateString,
        period: "monthly"
      }
    });
    NativeClient.sendDebugLog("Action.ts getMonthlyUserRanking month = " +  yearMonth + ", response = " + JSON.stringify(res.data));
    const storeData = new MypageStoreUserRanking();
    storeData.month = yearMonth;
    storeData.data = res.data;
    commit("updateMonthlyUserRanking", storeData);
  },
  // トップのレシピ閲覧履歴取得
  getTopHistoryRecipe: async ({ commit }) => {
    const startDate = getPreviou6Date();
    const startDateString = `${DateUtil.dateYYYYMMDDWithHiphen(startDate)}T00:00:00`;
    const res = await apiClient.get(API_URL_RECIPE.USER_HISTORY, {
      params: {
        // 集約
        groupBy: "both",
        // 取得開始位置（すでに取得済みの場合、取得した件数以降の開始位置を指定）
        start: 0,
        // 開始日は、一週間前（本日を含むため六日前）
        startDate: startDateString,
        // 数
        row: 6
      }
    });
    NativeClient.sendDebugLog("Action.ts getTopHistoryRecipe response = " + JSON.stringify(res.data));
    commit("updateTopHistoryRecipe", res.data);
  },
  // レシピ閲覧履歴取得
  getHistoryRecipe: async ({ commit }) => {
    const rowOneTime = 10;
    NativeClient.sendDebugLog("Action.ts getHistoryRecipe start");

    const previous: MypageHistoryRecipes | null = store.getters["mypage/getHistoryRecipe"];

    // 全取得済み判定と今回の取得開始位置
    let startPosition = 0;
    if (previous != null) {
      // 前回のデータが存在する
      NativeClient.sendDebugLog("Action.ts getHistoryRecipe start = " + previous.start + ", totalCount = " + previous.totalCount);
      if ((previous.start + rowOneTime >= previous.totalCount) || (previous.recipes.length >= previous.totalCount)) {
        // 既に全件取得ずみ
        return;
      }
      startPosition = previous.start + rowOneTime;
    }

    // 閲覧履歴一覧取得API呼び出し
    const res = await apiClient.get(API_URL_RECIPE.USER_HISTORY, {
      params: {
        // 集約
        groupBy: "both",
        // 取得開始位置（すでに取得済みの場合、取得した件数以降の開始位置を指定）
        start: startPosition,
        // 10件
        row: rowOneTime
      }
    });
    NativeClient.sendDebugLog("Action.ts getHistoryRecipe response = " + JSON.stringify(res.data));
    commit("updateHistoryRecipe", res.data);
  },
  // 閲覧履歴クリア
  clearHistoryRecipe: ({ commit }) => {
    NativeClient.sendDebugLog("Action.ts clearHistoryRecipeList");
    commit("updateHistoryRecipe", null);
  },
  // 表示月更新
  updateSelectedMonthIndex: ({ commit }, index: number | null) => {
    NativeClient.sendDebugLog("Action.ts updateSelectedMonthIndex index = " + index);
    commit("updateSelectedMonthIndex", index);
  }
};

export default actions;
